import { FuelButton } from '@frete.com/fuel-react';
import { Illustration } from 'components/Illustration';
import { SearchTruckerMessageError } from 'config/truckerProfileService';
import { TruckerAway } from 'constants/enums/trucker';
import { SUGGESTED_TAB } from 'context/reducers/tabs.type';
import { useAppContext } from 'hooks/useAppContext';
import { useAppStates } from 'hooks/useAppStates';
import { useLoadTruckers } from 'hooks/useLoadTruckers';
import { useSearchTrucker } from 'hooks/useSearchTrucker';
import React, { FC } from 'react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import { dsTheme } from 'constants/styled.contants';
import SearchEmptyResult from '../SearchEmptyResult';
import { RADIUS_RANGE_STEPS, getDisplayRangeValue, getNextRangeValue } from '../constants';
import * as Styled from './styled';

const EmptyProfileWrapperContent: FC = () => {
  const { fetchTruckers } = useLoadTruckers('lazy');
  const { searchTrucker } = useSearchTrucker();
  const {
    state: {
      truckers: { radius },
      searchTruckers: { term, errorSearch },
      tabs: { activeTab },
    },
    dispatch,
  } = useAppContext();

  const { isEmptySuggested, isEmptySearchDriver, isFiltersApplied, isErrorLoadSuggested } = useAppStates();

  const changeRadius = () => {
    dispatch({ type: 'truckers_radius', payload: { radius: getNextRangeValue(radius) } });
    fetchTruckers(getNextRangeValue(radius), 'botao-aumentar-raio');
  };

  const retry = () => {
    if (activeTab === SUGGESTED_TAB) {
      dispatch({ type: 'truckers_radius', payload: { radius } });
      fetchTruckers(radius, 'botao-tentar-novamente-falha-carregamento');
    }
  };

  const searchTerm = () => {
    searchTrucker(term);
  };

  const handleGoBackToTuckersList = () => {
    dispatch({ type: 'search_truckers_clear' });
  };

  const messageMap = {
    suggested: {
      title: `Não encontramos motoristas no raio de ${radius === 0 ? 5 : radius}km da sua localização`,
      message: 'Aumente o raio de busca e tente novamente.',
    },
    trading: {
      title: 'Nenhuma negociação ainda',
      message: 'Para começar a negociar com um motorista, entre em contato com ele pela lista de motoristas sugeridos.',
    },
    multipleFilters: {
      title: 'Ajuste os filtros de pesquisa para ver resultados.',
    },
    dataError: {
      title: 'Falha no carregamento',
      message: 'Não foi possível carregar a lista de motoristas no momento. Por favor, tente novamente.',
    },
    searchDataError: {
      title: 'Falha no carregamento',
      message: 'Não foi possível carregar a busca de motoristas no momento. Por favor, tente novamente.',
    },
  };

  return (
    <>
      {isEmptySearchDriver ? (
        <SearchEmptyResult />
      ) : (
        <FuelFlex
          defaultProps={{
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${dsTheme.color.background.neutral.weak}`,
          }}
        >
          <FuelFlex
            defaultProps={{
              maxWidth: '490px',
              customPadding: `0 ${dsTheme.spacing.km12} 0 ${dsTheme.spacing.km12}`,
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {isEmptySuggested && !term && (
              <>
                <Styled.FilterEmptyStateImage />
                {isFiltersApplied ? (
                  <Styled.TitleCustom size="heading-md" weight="bold" tag="h4" color="neutral-strong">
                    {messageMap.multipleFilters.title}
                  </Styled.TitleCustom>
                ) : (
                  <>
                    <Styled.TitleCustom size="heading-md" weight="bold" tag="h4" color="neutral-strong">
                      {messageMap.suggested.title}
                    </Styled.TitleCustom>
                    {radius + RADIUS_RANGE_STEPS <= TruckerAway.MAX_VALUE && (
                      <>
                        <Styled.SubCustom size="body-sm" color="neutral-default">
                          {messageMap.suggested.message}
                        </Styled.SubCustom>

                        <FuelButton onClick={() => changeRadius()} variant="primary" size="large">
                          Aumentar raio para {getDisplayRangeValue(+radius)}km
                        </FuelButton>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {isErrorLoadSuggested && (
              <>
                <Illustration name="doubt-1" alt="Falha no carregamento" height={224} width={224} spacingBottom={90} />
                <Styled.TitleCustom size="heading-md" weight="bold" tag="h4" color="neutral-strong">
                  {messageMap.dataError.title}
                </Styled.TitleCustom>
                <Styled.SubCustom size="body-sm" color="neutral-default">
                  {messageMap.dataError.message}
                </Styled.SubCustom>
                <FuelButton onClick={() => retry()} variant="primary" size="large">
                  Tentar novamente
                </FuelButton>
              </>
            )}

            {errorSearch === SearchTruckerMessageError.undefined && (
              <>
                <Illustration
                  name="doubt-1"
                  alt="Falha na busca de motorista"
                  height={224}
                  width={224}
                  spacingBottom={90}
                />
                <Styled.TitleCustom size="heading-md" weight="bold" tag="h4" color="neutral-strong">
                  {messageMap.searchDataError.title}
                </Styled.TitleCustom>
                <Styled.SubCustom size="body-sm" color="neutral-default">
                  {messageMap.searchDataError.message}
                </Styled.SubCustom>
                <FuelFlex
                  defaultProps={{ alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 'km12' }}
                >
                  <FuelButton variant="tertiary" size="large" onClick={handleGoBackToTuckersList}>
                    Ir para lista de motoristas
                  </FuelButton>
                  <FuelButton onClick={() => searchTerm()} variant="primary" size="large">
                    Tentar novamente
                  </FuelButton>
                </FuelFlex>
              </>
            )}
          </FuelFlex>
        </FuelFlex>
      )}
    </>
  );
};

export default EmptyProfileWrapperContent;
